import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';
import type { ChatStatus } from '~/hooks/queries/use-chat-status';

/**
 * FRONT-661 — the single status light for a chat, now the five-state spine
 * (epic FRONT-626). One light, identical everywhere, driven by the server-side
 * chat-status spine:
 *
 *   - `working`     → blue, pulsing — a run is going
 *   - `done-unseen` → green, steady — finished, not opened since
 *   - `done-seen`   → grey, steady — seen already, or nothing going on
 *   - `needs-you`   → amber, fast-pulsing — paused on a permission/question;
 *                     the quick pulse pulls you back without a sound/modal
 *                     (FRONT-662)
 *   - `failed`      → red — the run failed
 *
 * The colours use the design-system tokens (one blue, one green, one amber,
 * one red) per the CLAUDE.md colour rule.
 */
export type ChatLightState =
  | 'working'
  | 'done-unseen'
  | 'done-seen'
  | 'needs-you'
  | 'failed';

/**
 * FRONT-705: derives the dot state for an asset's chat-status spine entry —
 * reads the same five-state spine `ChatLight` renders (the chat pills +
 * header indicator's source) instead of a two/three-state light derived from
 * a single background-chat run. Originally the Inbox row's own helper;
 * FRONT-1453 removed the Inbox dot but the Files-card status light
 * (`routes/files.tsx`'s `FileStatusDot`) still reads it, so it lives here
 * next to `ChatLightState`/`ChatLight` rather than in a page component.
 *
 * The guard is deliberately conservative: an item with NO status entry
 * (nobody has ever chatted with it — the batched fetch has nothing to say
 * about it) or a `done-seen` status with no `runId` (the spine has never seen
 * a run for it — the equivalent of "no background chat" in the old world)
 * both render NO dot, preserving the look for a never-chatted asset.
 * Pure so it's unit-testable without mounting anything.
 */
export function feedRowLightState(
  status: ChatStatus | undefined,
): ChatLightState | null {
  if (!status) return null;
  if (status.state === 'done-seen' && !status.runId) return null;
  return status.state;
}

const STYLES: Record<ChatLightState, { dot: string; ring: string; anim: string; label: string }> = {
  // ring-2 halo (not animated) makes the active lights prominent without the
  // whole row turning into a disco ball. Only the two attention states animate:
  // `working` with the slow calm pulse, `needs-you` with the quicker pull-back
  // pulse (FRONT-662) so the two read as clearly different at a glance.
  working: {
    dot: 'bg-primary',
    ring: 'ring-2 ring-primary/35',
    anim: 'animate-pulse',
    label: 'EDDY is working on this',
  },
  'done-unseen': {
    dot: 'bg-success',
    ring: 'ring-2 ring-success/35',
    anim: '',
    label: 'Answer ready',
  },
  'needs-you': {
    dot: 'bg-amber-500',
    ring: 'ring-2 ring-amber-500/35',
    anim: 'animate-pulse-fast',
    label: 'Waiting for you',
  },
  failed: {
    dot: 'bg-destructive',
    ring: 'ring-2 ring-destructive/35',
    anim: '',
    label: 'Something went wrong',
  },
  'done-seen': {
    dot: 'bg-muted-foreground/40',
    ring: '',
    anim: '',
    label: '',
  },
};

/**
 * The status dot itself. Default size is deliberately chunky (`size-2.5`) so
 * the light is easy to spot at a glance — bump `size` only if a host needs a
 * different footprint.
 */
export function ChatLight({
  state,
  size = 'size-2.5',
  className,
}: {
  state: ChatLightState;
  size?: string;
  className?: string;
}) {
  const s = STYLES[state];
  return (
    <Tip label={s.label || undefined}>
      <span
        data-testid="chat-status-light"
        aria-hidden={s.label ? undefined : true}
        aria-label={s.label || undefined}
        className={cn(
          'inline-block rounded-full shrink-0',
          size,
          s.dot,
          s.ring,
          s.anim,
          className,
        )}
      />
    </Tip>
  );
}
